/**
 * Message templates (docs/plans/TABLES-AND-AUTOMATIONS.md). Sigma ships one
 * default template per automatic message; a company can only edit its
 * subject and text (organization_message_template_edits). Layout, buttons,
 * links and the company's logo/details are never editable — they come from
 * the email layout and the company profile.
 *
 * Pure (no imports): edge functions render with it and the CRM uses it for
 * the editor and previews. A new template is added here and announced with
 * public.announce_message_template() in the migration that ships it.
 */

export type MessageTemplateChannel = "email" | "sms";

export type MessageTemplateVariable = {
  key: string;
  label: string;
  /** Shown in the editor preview. */
  sample: string;
  /** Drop the whole line when this value is empty (e.g. calendar link). */
  dropLineWhenEmpty?: boolean;
};

export type MessageTemplateDef = {
  key: string;
  /** App it belongs to (registry ModuleId). */
  moduleId: "invoices" | "calendar" | "tickets";
  /** Automation switch it belongs to, if any (registry automations). */
  automationKey?: string;
  label: string;
  description: string;
  channel: MessageTemplateChannel;
  /** Email subject (email only). */
  subject?: string;
  body: string;
  variables: MessageTemplateVariable[];
  /** Must stay in the message; added back at the end if removed. */
  required?: Array<{ key: string; label: string }>;
  /** YYYY-MM-DD it shipped: "New" badge for 30 days. */
  introducedAt: string;
};

const v = (
  key: string,
  label: string,
  sample: string,
  dropLineWhenEmpty = false,
): MessageTemplateVariable => ({ key, label, sample, dropLineWhenEmpty });

const CLIENT_FIRST_NAME = v("client_first_name", "Client first name", "Maria");
const COMPANY_NAME = v(
  "company_name",
  "Your company name",
  "Summit Ridge Roofing",
);
const INVOICE_NUMBER = v("invoice_number", "Invoice number", "INV-1024");
const AMOUNT = v("amount", "Amount", "$1,250.00");
const DUE_DATE = v("due_date", "Due date", "October 30, 2026");
const PAYMENT_FOR = v(
  "payment_for",
  "What the payment is for",
  "invoice INV-1024",
);
const PAYMENT_LINK = v(
  "payment_link",
  "Payment link",
  "https://www.nomicrm.com/i/abc123",
);
const PROPERTY_ADDRESS = v(
  "property_address",
  "Property address",
  "101 5th Street, Stamford, CT",
);
const INVOICE_TITLE = v(
  "invoice_title",
  "Invoice title",
  "Invoice for supplement",
);
const READY_LINE = v(
  "ready_line",
  "What is ready",
  "Your Xactimate supplement is ready.",
);
const SENDER_FIRST_NAME = v("sender_first_name", "Your first name", "Rita");
const MEETING_TITLE = v("meeting_title", "Meeting title", "Roof inspection");
const MEETING_WHEN = v(
  "meeting_when",
  "Date and time",
  "Tue, Oct 20 · 10:00 AM ET",
);
const MEETING_LINK = v(
  "meeting_link",
  "Meeting link",
  "https://www.nomicrm.com/m/xyz",
);
const CALENDAR_LINK = v(
  "calendar_link",
  "Add-to-calendar link",
  "https://www.nomicrm.com/c/xyz",
  true,
);
const HOST_FIRST_NAME = v("host_first_name", "Host first name", "Rita");
const MEETING_NOTES = v(
  "meeting_notes",
  "Meeting notes",
  "Please have the attic open.",
  true,
);

export const MESSAGE_TEMPLATES: MessageTemplateDef[] = [
  // ── Invoices ──────────────────────────────────────────────
  {
    key: "invoices.invoice.email",
    moduleId: "invoices",
    label: "Invoice email",
    description: "The email your client gets when you send an invoice.",
    channel: "email",
    subject: "{{company_name}}: Invoice {{invoice_number}}",
    body: "Hi {{client_first_name}},\n\nThank you for your business. Your invoice is ready — a PDF copy is attached for your records.\n\nYou can pay securely online with the link below.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      INVOICE_NUMBER,
      AMOUNT,
      DUE_DATE,
    ],
    introducedAt: "2026-10-14",
  },
  {
    key: "invoices.invoice.sms",
    moduleId: "invoices",
    automationKey: "textInvoices",
    label: "Invoice text",
    description: "The text your client gets when you send an invoice by SMS.",
    channel: "sms",
    body: "Hi {{client_first_name}},\n{{company_name}} here: your invoice {{invoice_number}} is ready — {{amount}} due {{due_date}}.\nPay: {{payment_link}}\n\nThank you for your trust.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      SENDER_FIRST_NAME,
      INVOICE_NUMBER,
      AMOUNT,
      DUE_DATE,
      PAYMENT_LINK,
    ],
    required: [{ key: "payment_link", label: "Pay" }],
    introducedAt: "2026-10-14",
  },
  {
    key: "invoices.reminder.upcoming",
    moduleId: "invoices",
    automationKey: "paymentReminders",
    label: "Payment reminder — before the due date",
    description: "Sent 3 days and 1 day before an invoice is due.",
    channel: "email",
    subject: "{{company_name}}: Upcoming payment · {{invoice_number}}",
    body: "Reminder: {{amount}} for {{payment_for}} is due on {{due_date}}.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      INVOICE_NUMBER,
      AMOUNT,
      DUE_DATE,
      PAYMENT_FOR,
    ],
    introducedAt: "2026-10-14",
  },
  {
    key: "invoices.reminder.due_today",
    moduleId: "invoices",
    automationKey: "paymentReminders",
    label: "Payment reminder — due today",
    description: "Sent on the due date.",
    channel: "email",
    subject: "{{company_name}}: Payment due today · {{invoice_number}}",
    body: "Your payment of {{amount}} for {{payment_for}} is due today.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      INVOICE_NUMBER,
      AMOUNT,
      DUE_DATE,
      PAYMENT_FOR,
    ],
    introducedAt: "2026-10-14",
  },
  {
    key: "invoices.reminder.overdue",
    moduleId: "invoices",
    automationKey: "paymentReminders",
    label: "Payment reminder — overdue",
    description: "Sent when an invoice is past due.",
    channel: "email",
    subject: "{{company_name}}: Payment overdue · {{invoice_number}}",
    body: "Your payment of {{amount}} for {{payment_for}} was due on {{due_date}}. Please pay at your earliest convenience.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      INVOICE_NUMBER,
      AMOUNT,
      DUE_DATE,
      PAYMENT_FOR,
    ],
    introducedAt: "2026-10-14",
  },
  {
    key: "invoices.receipt.email",
    moduleId: "invoices",
    label: "Payment receipt email",
    description: "Sent when a payment comes in (always by email).",
    channel: "email",
    subject: "{{company_name}}: Payment receipt · {{invoice_number}}",
    body: "Thank you for your payment.",
    variables: [CLIENT_FIRST_NAME, COMPANY_NAME, INVOICE_NUMBER, AMOUNT],
    introducedAt: "2026-10-14",
  },
  {
    key: "invoices.receipt.sms",
    moduleId: "invoices",
    automationKey: "textReceipts",
    label: "Payment receipt text",
    description: "Text sent when a payment comes in.",
    channel: "sms",
    body: "{{company_name}}: payment received for {{invoice_number}}. Your receipt: {{receipt_link}}",
    variables: [
      COMPANY_NAME,
      INVOICE_NUMBER,
      AMOUNT,
      v("receipt_link", "Receipt link", "https://www.nomicrm.com/r/abc123"),
    ],
    required: [{ key: "receipt_link", label: "Receipt" }],
    introducedAt: "2026-10-14",
  },
  // ── Tickets ───────────────────────────────────────────────
  // Defaults match the copy built per service; an edit replaces it.
  {
    key: "tickets.invoice.email",
    moduleId: "tickets",
    label: "Ticket invoice email",
    description: "Sent with the payment link when you invoice a ticket.",
    channel: "email",
    subject: "{{invoice_title}} ({{property_address}})",
    body: "{{ready_line}}\n\nPlease pay using the secure link below. After payment you will receive one email with your files attached.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      PROPERTY_ADDRESS,
      INVOICE_TITLE,
      READY_LINE,
    ],
    introducedAt: "2026-10-29",
  },
  {
    key: "tickets.reminder.email",
    moduleId: "tickets",
    label: "Ticket invoice reminder",
    description: "Reminder for a ticket invoice that is still unpaid.",
    channel: "email",
    subject: "Reminder: {{invoice_title}} ({{property_address}})",
    body: "This is a friendly reminder that your invoice is still unpaid.\n\nPlease pay using the secure link below. After payment you will receive one email with your files attached.",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      PROPERTY_ADDRESS,
      INVOICE_TITLE,
    ],
    introducedAt: "2026-10-29",
  },
  {
    key: "tickets.delivery.email",
    moduleId: "tickets",
    label: "Files delivered email",
    description: "Sent with the files attached once the ticket is paid.",
    channel: "email",
    subject: "Your project files are ready ({{property_address}})",
    body: "Hi {{client_first_name}},\n\n{{files_status}}",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      PROPERTY_ADDRESS,
      INVOICE_NUMBER,
      v(
        "files_status",
        "Where the files are",
        "Your files for 101 5th Street are attached to this email. You can also find them anytime in your portal.",
      ),
    ],
    introducedAt: "2026-10-29",
  },
  {
    key: "tickets.portal_code.email",
    moduleId: "tickets",
    label: "Portal sign-in code",
    description: "The code a client types to open their portal.",
    channel: "email",
    subject: "Your {{company_name}} verification code",
    body: "Your verification code is: {{code}}\n\nThis code expires in 10 minutes.",
    variables: [COMPANY_NAME, v("code", "Verification code", "482913")],
    required: [{ key: "code", label: "Code" }],
    introducedAt: "2026-10-29",
  },
  // ── Calendar ──────────────────────────────────────────────
  {
    key: "calendar.meeting.invite.email",
    moduleId: "calendar",
    label: "Meeting invite email",
    description: "Email invite your client gets for a scheduled video call.",
    channel: "email",
    subject: "{{company_name}}: {{meeting_title}}",
    body: "Hi {{client_first_name}},\n\n{{meeting_notes}}\n\n{{meeting_when}}\n\nJoin our video call using the link below.\n\n{{host_first_name}} from {{company_name}}",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      HOST_FIRST_NAME,
      MEETING_TITLE,
      MEETING_WHEN,
      MEETING_NOTES,
    ],
    introducedAt: "2026-10-14",
  },
  {
    key: "calendar.meeting.reschedule.email",
    moduleId: "calendar",
    label: "Meeting rescheduled email",
    description: "Email your client gets when you move a video call.",
    channel: "email",
    subject: "{{company_name}}: Rescheduled — {{meeting_title}}",
    body: "Hi {{client_first_name}},\n\n{{meeting_notes}}\n\n{{meeting_when}}\n\nYour video call was rescheduled. Join using the link below.\n\n{{host_first_name}} from {{company_name}}",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      HOST_FIRST_NAME,
      MEETING_TITLE,
      MEETING_WHEN,
      MEETING_NOTES,
    ],
    introducedAt: "2026-10-14",
  },
  {
    key: "calendar.meeting.confirmation.sms",
    moduleId: "calendar",
    label: "Meeting confirmation text",
    description: "Text your client gets when you schedule a video call.",
    channel: "sms",
    body: "Hi {{client_first_name}},\n\n{{meeting_notes}}\nYou're booked for a video call:\n{{meeting_when}}\n\nJoin: {{meeting_link}}\nAdd to calendar: {{calendar_link}}\n\n{{host_first_name}} from {{company_name}}",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      HOST_FIRST_NAME,
      MEETING_WHEN,
      MEETING_NOTES,
      MEETING_LINK,
      CALENDAR_LINK,
    ],
    required: [{ key: "meeting_link", label: "Join" }],
    introducedAt: "2026-10-14",
  },
  {
    key: "calendar.meeting.reschedule.sms",
    moduleId: "calendar",
    label: "Meeting rescheduled text",
    description: "Text your client gets when you move a video call.",
    channel: "sms",
    body: "Hi {{client_first_name}},\n\n{{meeting_notes}}\nYour video call was rescheduled:\n{{meeting_when}}\n\nJoin: {{meeting_link}}\nAdd to calendar: {{calendar_link}}\n\n{{host_first_name}} from {{company_name}}",
    variables: [
      CLIENT_FIRST_NAME,
      COMPANY_NAME,
      HOST_FIRST_NAME,
      MEETING_WHEN,
      MEETING_NOTES,
      MEETING_LINK,
      CALENDAR_LINK,
    ],
    required: [{ key: "meeting_link", label: "Join" }],
    introducedAt: "2026-10-14",
  },
  {
    key: "calendar.meeting.reminder15.sms",
    moduleId: "calendar",
    label: "Meeting reminder text — 15 minutes",
    description: "Text your client gets 15 minutes before a video call.",
    channel: "sms",
    body: "Reminder: video call with {{host_first_name}} starts in 15 minutes ({{meeting_time}}).\n\nJoin: {{meeting_link}}\nAdd to calendar: {{calendar_link}}",
    variables: [
      HOST_FIRST_NAME,
      COMPANY_NAME,
      v("meeting_time", "Start time", "10:00 AM ET"),
      MEETING_LINK,
      CALENDAR_LINK,
    ],
    required: [{ key: "meeting_link", label: "Join" }],
    introducedAt: "2026-10-14",
  },
  {
    key: "calendar.meeting.reminder5.sms",
    moduleId: "calendar",
    label: "Meeting reminder text — 5 minutes",
    description: "Text your client gets 5 minutes before a video call.",
    channel: "sms",
    body: "Your video call starts in 5 minutes.\n\nJoin now: {{meeting_link}}",
    variables: [HOST_FIRST_NAME, COMPANY_NAME, MEETING_LINK],
    required: [{ key: "meeting_link", label: "Join" }],
    introducedAt: "2026-10-14",
  },
  {
    key: "calendar.booking.reminder.sms",
    moduleId: "calendar",
    automationKey: "bookingReminders",
    label: "Booking reminder text",
    description: "Text to people who booked through your booking link.",
    channel: "sms",
    body: "Reminder: your appointment is in {{minutes}} minutes.\n\nService: {{service_name}}\nWhen: {{appointment_when}}\nWith: {{host_first_name}}",
    variables: [
      v("minutes", "Minutes before", "15"),
      v("service_name", "Service", "Roof inspection"),
      v("appointment_when", "Date and time", "Tue, Oct 20 · 10:00 AM"),
      v("host_first_name", "Host first name", "Rita", true),
      COMPANY_NAME,
    ],
    introducedAt: "2026-10-14",
  },
];

export const messageTemplateByKey = (key: string) =>
  MESSAGE_TEMPLATES.find((template) => template.key === key) ?? null;

/** A company's edits (null fields = default). */
export type MessageTemplateEdit = {
  subject?: string | null;
  body?: string | null;
};

/** Subject and text in effect: the company's edit, else the default. */
export const effectiveTemplate = (
  def: MessageTemplateDef,
  edit?: MessageTemplateEdit | null,
) => ({
  subject: edit?.subject?.trim() || def.subject || "",
  body: edit?.body?.trim() || def.body,
  edited: Boolean(edit?.subject?.trim() || edit?.body?.trim()),
});

const VARIABLE_PATTERN = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;

/**
 * Fills {{variables}}. Lines whose `dropLineWhenEmpty` variables are empty
 * are removed; required links missing from the text are added at the end;
 * "Hi ," style leftovers are tidied.
 */
export const renderMessageTemplate = (
  def: MessageTemplateDef,
  text: string,
  values: Record<string, string | null | undefined>,
) => {
  const droppable = new Set(
    def.variables
      .filter((variable) => variable.dropLineWhenEmpty)
      .map((variable) => variable.key),
  );
  const value = (key: string) => String(values[key] ?? "").trim();

  let source = text;
  for (const required of def.required ?? []) {
    if (!new RegExp(`\\{\\{\\s*${required.key}\\s*\\}\\}`, "i").test(source)) {
      source = `${source.trimEnd()}\n\n${required.label}: {{${required.key}}}`;
    }
  }

  const lines = source.split("\n").filter((line) => {
    const keys = [...line.matchAll(VARIABLE_PATTERN)].map((match) =>
      match[1].toLowerCase(),
    );
    return !keys.some((key) => droppable.has(key) && !value(key));
  });

  return lines
    .join("\n")
    .replace(VARIABLE_PATTERN, (_, key: string) => value(key.toLowerCase()))
    .replace(/[ \t]*\([ \t]*\)/g, "")
    .replace(/[ \t]+([,.:;!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

/**
 * A company's edit of one field, filled with values; the copy built in code
 * when the field was not edited (so defaults keep their per-case wording).
 */
export const applyTemplateEdit = (
  key: string,
  edit: MessageTemplateEdit | null | undefined,
  values: Record<string, string | null | undefined>,
  fallback: { subject: string; body: string },
) => {
  const def = messageTemplateByKey(key);
  if (!def) return fallback;
  return {
    subject: edit?.subject?.trim()
      ? renderMessageTemplate(def, edit.subject, values)
      : fallback.subject,
    body: edit?.body?.trim()
      ? renderMessageTemplate(def, edit.body, values)
      : fallback.body,
  };
};

/** Sample values for previews in the editor. */
export const sampleTemplateValues = (
  def: MessageTemplateDef,
  overrides: Record<string, string> = {},
) => ({
  ...Object.fromEntries(
    def.variables.map((variable) => [variable.key, variable.sample]),
  ),
  ...overrides,
});
